import React, { useCallback, useEffect, useState } from "react";
import { ExternalLink, Send, Copy, Printer, Trash2, RefreshCw, Sparkles, Check, AlertTriangle, Eye, FileText, Pencil, X, Plus, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtNOK, fmtPct, fmtDate, relTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, Card, StatusChip, AIButton, Skeleton, Segmented } from "@/components/common";
import { ProposalDocument } from "@/components/proposal/ProposalDocument";
import { cn } from "@/lib/utils";

const TEXT_SECTIONS = [
  ["executive_summary", "Executive summary"],
  ["our_understanding", "Our understanding"],
  ["proposed_solution", "Proposed solution"],
  ["system_design", "System design"],
  ["scope_hardware", "Scope - hardware"],
  ["scope_software", "Scope - software"],
  ["scope_services", "Scope - services"],
];
const LIST_SECTIONS = [
  ["key_benefits", "Key benefits"],
  ["optional_items", "Options (not in scope)"],
  ["assumptions", "Assumptions"],
  ["exclusions", "Exclusions"],
  ["terms", "Terms"],
];
const GEN_STEPS = ["Reading requirements and solution", "Writing executive summary", "Describing the solution and system design", "Setting scope, assumptions and terms", "Laying out the proposal"];

function SectionEditor({ label, value, isList, onSave, testId }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState("");
  const start = () => {
    setV(isList ? (value || []).join("\n") : value || "");
    setEditing(true);
  };
  const save = async () => {
    const out = isList ? v.split("\n").map((s) => s.trim()).filter(Boolean) : v;
    await onSave(out);
    setEditing(false);
  };
  return (
    <section className="group" data-testid={testId}>
      <div className="mb-1.5 flex items-center justify-between">
        <h3 className="text-sm font-semibold">{label}</h3>
        {!editing && <button type="button" onClick={start} className="inline-flex items-center gap-1 text-xs text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100" data-testid={`${testId}-edit-button`}><Pencil className="h-3 w-3" /> Edit</button>}
      </div>
      {editing ? (
        <div className="space-y-2">
          <Textarea value={v} onChange={(e) => setV(e.target.value)} rows={isList ? Math.max(4, v.split("\n").length + 1) : 6} className="rounded-xl text-sm leading-6" autoFocus data-testid={`${testId}-input`} />
          {isList && <div className="text-xs text-muted-foreground">One item per line.</div>}
          <div className="flex gap-2"><Button size="sm" onClick={save} className="gap-1 bg-primary text-white hover:bg-[#ff8e76]" data-testid={`${testId}-save-button`}><Check className="h-3.5 w-3.5" /> Save</Button><Button size="sm" variant="ghost" onClick={() => setEditing(false)}><X className="h-3.5 w-3.5" /></Button></div>
        </div>
      ) : isList ? (
        (value || []).length ? <ul className="list-disc space-y-1 pl-5 text-sm leading-6">{value.map((x, i) => <li key={i}>{x}</li>)}</ul> : <p className="text-sm italic text-muted-foreground">Empty - click edit to add.</p>
      ) : (
        <p className="whitespace-pre-line text-sm leading-6">{value || <span className="italic text-muted-foreground">Empty - click edit to write.</span>}</p>
      )}
    </section>
  );
}

export const QuoteTab = ({ data, reload, setTab }) => {
  const o = data.opportunity;
  const versions = data.quotes || [];
  const solutions = data.solutions || [];
  const activeSol = solutions.find((s) => s.is_active) || solutions[0];
  const [selectedId, setSelectedId] = useState(versions[0]?.id || null);
  const [quote, setQuote] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genStep, setGenStep] = useState(0);
  const [genSolution, setGenSolution] = useState(activeSol?.id || "");
  const [sendOpen, setSendOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [recipient, setRecipient] = useState(data.contacts?.[0]?.id || "");
  const [message, setMessage] = useState("");

  const firstVersionId = versions[0]?.id;
  useEffect(() => {
    if (!selectedId && firstVersionId) setSelectedId(firstVersionId);
  }, [firstVersionId, selectedId]);
  const [view, setView] = useState("preview"); // preview | edit
  const [company, setCompany] = useState(null);
  useEffect(() => {
    api.get("/settings").then((r) => setCompany(r.data?.company || {})).catch(() => setCompany({}));
  }, []);
  const owner = (data.users || []).find((u) => u.id === o.owner_id) || {};

  const loadQuote = useCallback(async (id) => {
    if (!id) return;
    try {
      const r = await api.get(`/quotes/${id}`);
      setQuote(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, []);

  useEffect(() => {
    setQuote(null);
    if (selectedId) loadQuote(selectedId);
  }, [selectedId, loadQuote]);

  useEffect(() => {
    if (!generating) return undefined;
    setGenStep(0);
    const t = setInterval(() => setGenStep((s) => Math.min(s + 1, GEN_STEPS.length - 1)), 4500);
    return () => clearInterval(t);
  }, [generating]);

  const generate = async () => {
    if (!activeSol) {
      toast.error("Build a solution first");
      setTab("solution");
      return;
    }
    setGenerating(true);
    try {
      const r = await api.post(`/opportunities/${o.id}/quotes/generate`, { solution_id: genSolution || activeSol.id });
      toast.success(`Quote v${r.data.version} drafted`, { description: "AI wrote the proposal text from requirements and solution. Review before sending." });
      await reload();
      setSelectedId(r.data.id);
      setQuote(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setGenerating(false);
    }
  };

  const patch = async (body) => {
    try {
      const r = await api.patch(`/quotes/${quote.id}`, body);
      setQuote(r.data);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const approve = async () => {
    try {
      const r = await api.post(`/quotes/${quote.id}/approve`);
      setQuote(r.data);
      toast.success("Quote approved");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post(`/quotes/${quote.id}/send`);
      setQuote(r.data);
      setSendOpen(false);
      toast.success(`Quote v${r.data.version} sent`, { description: "Opportunity moved to Quote. Follow-up added as next step." });
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSending(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/quotes/${quote.id}`);
      toast.success("Draft deleted");
      setSelectedId(null);
      setQuote(null);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const publicUrl = quote ? `${window.location.origin}/q/${quote.token}` : "";
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  if (generating) {
    return (
      <div className="mx-auto max-w-lg py-16 text-center" data-testid="quote-generating">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary"><Sparkles className="h-6 w-6 animate-pulse" /></span>
        <h2 className="mt-5 text-xl font-semibold">Writing your proposal</h2>
        <p className="mt-1 text-sm text-muted-foreground">Usually 20-40 seconds. The text is grounded in the requirements and the active solution.</p>
        <ol className="mx-auto mt-6 max-w-xs space-y-2 text-left text-sm">
          {GEN_STEPS.map((s, i) => (
            <li key={s} className={cn("flex items-center gap-2", i > genStep && "text-muted-foreground/60")}>
              {i < genStep ? <Check className="h-4 w-4 text-[hsl(var(--status-ok))]" /> : i === genStep ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-primary" /> : <span className="h-4 w-4 rounded-full border" />}
              {s}
            </li>
          ))}
        </ol>
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <EmptyState
        icon={FileText}
        title="No quote yet"
        description={activeSol ? `Generate a versioned, digital proposal from '${activeSol.name}' (${fmtNOK(activeSol.totals?.total_sales)} NOK). The AI writes the narrative; you review and send.` : "Build a solution first - the quote is generated from it."}
        action={activeSol ? <AIButton onClick={generate} className="bg-primary text-white hover:bg-[#ff8e76]" data-testid="quote-empty-generate-button">Generate quote</AIButton> : <Button onClick={() => setTab("solution")} className="bg-primary text-white hover:bg-[#ff8e76]">Go to solution</Button>}
        secondary={solutions.length > 1 && activeSol ? (
          <Select value={genSolution || activeSol.id} onValueChange={setGenSolution}>
            <SelectTrigger className="h-9 w-44 rounded-[10px] text-sm" data-testid="quote-solution-select"><SelectValue /></SelectTrigger>
            <SelectContent>{solutions.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        ) : null}
        testId="quote-empty"
      />
    );
  }

  const t = quote?.totals;
  const needsApproval = quote?.approval?.required && !quote?.approved;
  const isDraft = quote?.status === "draft";

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr]">
      <aside>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold">Versions</h2>
          <AIButton size="sm" variant="outline" onClick={generate} className="h-8" data-testid="quote-new-version-button">New version</AIButton>
        </div>
        {solutions.length > 1 && (
          <Select value={genSolution || activeSol?.id} onValueChange={setGenSolution}>
            <SelectTrigger className="mb-3 h-8 rounded-[10px] text-xs" data-testid="quote-solution-select"><SelectValue /></SelectTrigger>
            <SelectContent>{solutions.map((s) => <SelectItem key={s.id} value={s.id}>From {s.name} - {fmtNOK(s.totals?.total_sales)}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <ol className="space-y-1.5" data-testid="quote-version-list">
          {versions.map((v) => (
            <li key={v.id}>
              <button type="button" onClick={() => setSelectedId(v.id)} data-testid={`quote-version-${v.version}`} className={cn("w-full rounded-xl border bg-white p-3 text-left hover:bg-muted/50", selectedId === v.id && "border-foreground/30 ring-1 ring-foreground/10")}>
                <div className="flex items-center justify-between"><span className="text-sm font-semibold">v{v.version}</span><StatusChip status={v.status} /></div>
                <div className="mt-1 text-sm tabular">{fmtNOK(v.totals?.total_sales)} NOK</div>
                <div className="mt-0.5 text-xs text-muted-foreground">{v.sent_at ? `Sent ${fmtDate(v.sent_at)}` : `Drafted ${relTime(v.created_at)}`}{v.views ? ` · ${v.views} view${v.views === 1 ? "" : "s"}` : ""}</div>
              </button>
            </li>
          ))}
        </ol>
      </aside>

      <div className="min-w-0">
        {!quote ? (
          <div className="space-y-4"><Skeleton className="h-10 w-2/3" /><Skeleton className="h-24" /><Skeleton className="h-64" /></div>
        ) : (
          <div className="space-y-8" data-testid="quote-editor">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground"><StatusChip status={quote.status} /> Version {quote.version}{quote.solution_name ? ` · from ${quote.solution_name}` : ""} · valid until <Input type="date" value={quote.valid_until || ""} onChange={(e) => patch({ valid_until: e.target.value })} className="h-7 w-[140px] rounded-md px-2 text-xs tabular" disabled={!isDraft} data-testid="quote-valid-until-input" /></div>
                <input value={quote.title || ""} onChange={(e) => setQuote({ ...quote, title: e.target.value })} onBlur={(e) => e.target.value !== (versions.find((v) => v.id === quote.id)?.title || "") && patch({ title: e.target.value })} disabled={!isDraft} className="mt-2 w-full bg-transparent text-2xl font-semibold tracking-[-0.02em] outline-none disabled:opacity-100" style={{ fontFamily: "var(--font-display)" }} data-testid="quote-title-input" />
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" className="gap-1.5" asChild><a href={`/q/${quote.token}`} target="_blank" rel="noreferrer" data-testid="quote-open-public-link"><ExternalLink className="h-3.5 w-3.5" /> Open in new tab</a></Button>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={copyLink} data-testid="quote-copy-link-button"><Copy className="h-3.5 w-3.5" /> Copy link</Button>
                <Button variant="outline" size="sm" className="gap-1.5" asChild><a href={`/q/${quote.token}?print=1`} target="_blank" rel="noreferrer" data-testid="quote-print-link"><Printer className="h-3.5 w-3.5" /> PDF</a></Button>
                {isDraft && <Button size="sm" disabled={needsApproval} onClick={() => setSendOpen(true)} className="gap-1.5 bg-primary text-white hover:bg-[#ff8e76]" data-testid="quote-send-button"><Send className="h-3.5 w-3.5" /> Send to customer</Button>}
                {isDraft && <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={remove} title="Delete draft" data-testid="quote-delete-button"><Trash2 className="h-4 w-4" /></Button>}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <Segmented items={[{ key: "preview", label: "Preview" }, { key: "edit", label: "Edit text" }]} value={view} onChange={setView} testIdPrefix="quote-view" />
              <div className="flex items-center gap-2 text-xs text-muted-foreground"><Eye className="h-3.5 w-3.5" /> {quote.views ? `Viewed ${quote.views} time${quote.views === 1 ? "" : "s"} by the customer${quote.last_viewed_at ? `, last ${relTime(quote.last_viewed_at)}` : ""}.` : "Not yet viewed by the customer."}</div>
            </div>

            {quote.customer_accepted_at && (
              <div className="flex items-center gap-2 rounded-xl border border-[hsl(var(--status-ok)/0.3)] bg-[hsl(var(--status-ok)/0.06)] px-4 py-2.5 text-sm" data-testid="quote-customer-accepted-banner"><BadgeCheck className="h-4 w-4 text-[hsl(var(--status-ok))]" /> Accepted online by <span className="font-medium">{quote.customer_accepted_by || "customer"}</span> on {fmtDate(quote.customer_accepted_at, { year: true })}. Mark the opportunity as Won.</div>
            )}

            {quote.approval?.required && (
              <div className={cn("flex flex-col gap-3 rounded-xl border px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between", quote.approved ? "border-[hsl(var(--status-ok)/0.3)] bg-[hsl(var(--status-ok)/0.06)]" : "border-[hsl(var(--status-warn)/0.3)] bg-[hsl(var(--status-warn)/0.06)]")} data-testid="quote-approval-banner">
                <div className="flex items-start gap-2">{quote.approved ? <Check className="mt-0.5 h-4 w-4 text-[hsl(var(--status-ok))]" /> : <AlertTriangle className="mt-0.5 h-4 w-4 text-[hsl(var(--status-warn))]" />}<div><span className="font-medium">{quote.approval.label}</span>{quote.approval.reason ? <span className="text-muted-foreground"> - {quote.approval.reason}</span> : null}{quote.approved && <span className="text-muted-foreground"> · approved by {quote.approved_by}</span>}</div></div>
                {!quote.approved && <Button size="sm" variant="outline" className="shrink-0 gap-1.5 bg-white" onClick={approve} data-testid="quote-approve-button"><BadgeCheck className="h-3.5 w-3.5" /> Approve</Button>}
              </div>
            )}

            <Card className="grid grid-cols-2 gap-4 p-4 text-sm sm:grid-cols-5" data-testid="quote-totals">
              <div><div className="text-[11px] uppercase tracking-wide text-muted-foreground">Hardware</div><div className="tabular font-medium">{fmtNOK(t.hardware)}</div></div>
              <div><div className="text-[11px] uppercase tracking-wide text-muted-foreground">Software</div><div className="tabular font-medium">{fmtNOK(t.software)}</div></div>
              <div><div className="text-[11px] uppercase tracking-wide text-muted-foreground">Services</div><div className="tabular font-medium">{fmtNOK(t.services)}</div></div>
              <div><div className="text-[11px] uppercase tracking-wide text-muted-foreground">Total excl. VAT</div><div className="text-base font-semibold tabular" data-testid="quote-total-value">{fmtNOK(t.total_sales)} NOK</div></div>
              <div><div className="text-[11px] uppercase tracking-wide text-muted-foreground">GM (internal)</div><div className={cn("tabular font-medium", t.gm_pct < 30 ? "text-[hsl(var(--status-warn))]" : "text-[hsl(var(--status-ok))]")} data-testid="quote-gm-value">{fmtPct(t.gm_pct)}</div></div>
            </Card>

            {isDraft && activeSol && Math.round(activeSol.totals?.total_sales || 0) !== Math.round(t.total_sales || 0) && (
              <div className="flex items-center justify-between gap-3 rounded-xl border border-primary/25 bg-primary/[0.06] px-4 py-2.5 text-sm" data-testid="quote-refresh-banner">
                <span>The solution has changed since this draft ({fmtNOK(activeSol.totals.total_sales)} NOK now). Refresh the items and prices?</span>
                <Button size="sm" variant="outline" className="shrink-0 gap-1.5 bg-white" onClick={() => patch({ refresh_items: true })} data-testid="quote-refresh-items-button"><RefreshCw className="h-3.5 w-3.5" /> Refresh items</Button>
              </div>
            )}

            {view === "preview" ? (
              <div className="overflow-hidden rounded-[22px] border border-black/[0.08] bg-background shadow-[0_1px_2px_hsl(30_12%_18%/0.04),0_24px_60px_-40px_hsl(30_12%_18%/0.4)]" data-testid="quote-preview">
                <div className="px-5 py-8 sm:px-10 sm:py-12">
                  <ProposalDocument data={{ quote, opportunity: { name: o.name, summary: o.summary, expected_close_date: o.expected_close_date }, account: data.account || {}, owner, contacts: data.contacts || [], company: company || {} }} />
                </div>
              </div>
            ) : (
            <>
            <div className="grid grid-cols-1 gap-8 xl:grid-cols-2">
              {TEXT_SECTIONS.map(([k, label]) => (
                <SectionEditor key={k} label={label} value={quote.sections?.[k]} onSave={(v) => patch({ sections: { [k]: v } })} testId={`quote-section-${k}`} />
              ))}
            </div>

            <section data-testid="quote-items">
              <h3 className="mb-2 text-sm font-semibold">Solution overview (as it appears in the proposal)</h3>
              <Card className="overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs text-muted-foreground"><tr><th className="px-4 py-2 text-left font-medium">Item</th><th className="px-4 py-2 text-right font-medium">Qty</th><th className="px-4 py-2 text-right font-medium">Unit price</th><th className="px-4 py-2 text-right font-medium">Total</th></tr></thead>
                  <tbody>
                    {(quote.items_snapshot || []).map((g) => (
                      <React.Fragment key={g.id}>
                        <tr className="bg-muted/20"><td colSpan={4} className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{g.name}</td></tr>
                        {(g.items || []).map((it) => {
                          const unit = it.sales_price * (1 - (it.discount_pct || 0) / 100);
                          return (
                            <tr key={it.id} className="border-t"><td className="px-4 py-2"><div className="font-medium">{it.name}</div><div className="text-xs text-muted-foreground">{it.manufacturer}{it.reason ? ` · ${it.reason}` : ""}</div></td><td className="whitespace-nowrap px-4 py-2 text-right tabular">{it.qty} {it.unit !== "pcs" ? it.unit : ""}</td><td className="whitespace-nowrap px-4 py-2 text-right tabular">{fmtNOK(unit)}{it.discount_pct ? <span className="ml-1 text-xs text-muted-foreground">(-{it.discount_pct}%)</span> : null}</td><td className="whitespace-nowrap px-4 py-2 text-right tabular font-medium">{fmtNOK(unit * it.qty)}</td></tr>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </Card>
            </section>

            <div className="grid grid-cols-1 gap-8 xl:grid-cols-2">
              {LIST_SECTIONS.map(([k, label]) => (
                <SectionEditor key={k} label={label} isList value={quote.sections?.[k]} onSave={(v) => patch({ sections: { [k]: v } })} testId={`quote-section-${k}`} />
              ))}
              <SectionEditor label="Timeline" isList value={(quote.sections?.timeline || []).map((x) => `${x.phase} | ${x.duration}`)} onSave={(lines) => patch({ sections: { timeline: lines.map((l) => { const [phase, duration] = l.split("|").map((s) => s.trim()); return { phase: phase || l, duration: duration || "" }; }) } })} testId="quote-section-timeline" />
            </div>
            </>
            )}
          </div>
        )}
      </div>

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="quote-send-dialog">
          <DialogHeader><DialogTitle>Send quote v{quote?.version}</DialogTitle><DialogDescription>Marks the quote as sent, moves the opportunity to Quote and adds a follow-up. E-mail delivery is simulated in this demo - the link below is live.</DialogDescription></DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <div className="mb-1 text-xs text-muted-foreground">Recipient</div>
              {data.contacts?.length ? (
                <Select value={recipient} onValueChange={setRecipient}>
                  <SelectTrigger className="h-9 rounded-[10px]" data-testid="quote-send-recipient-select"><SelectValue placeholder="Choose contact" /></SelectTrigger>
                  <SelectContent>{data.contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} {c.email ? `<${c.email}>` : ""}</SelectItem>)}</SelectContent>
                </Select>
              ) : <div className="text-muted-foreground">No contacts on this customer yet.</div>}
            </div>
            <div>
              <div className="mb-1 text-xs text-muted-foreground">Message</div>
              <Textarea rows={5} value={message || `Hi,\n\nPlease find our proposal for ${o.name} here: ${publicUrl}\n\nIt is valid until ${quote?.valid_until ? fmtDate(quote.valid_until, { year: true }) : ""}. Happy to walk you through it.\n\nBest regards,\n${o.owner_name}`} onChange={(e) => setMessage(e.target.value)} className="rounded-xl text-sm" data-testid="quote-send-message-input" />
            </div>
          </div>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setSendOpen(false)}>Cancel</Button><Button onClick={send} disabled={sending} className="gap-1.5 bg-primary text-white hover:bg-[#ff8e76]" data-testid="quote-send-confirm-button"><Send className="h-3.5 w-3.5" /> {sending ? "Sending..." : "Send quote"}</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuoteTab;
